if (!customElements.get('plan-cards')) {
  class PlanCards extends HTMLElement {
    connectedCallback() {
      this.action = this.dataset.planCardsAction || 'checkout';
      this.onClick = this.onClick.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.addEventListener('click', this.onClick);
      window.addEventListener('pageshow', this.onPageShow);
      this.initCounters();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    onPageShow() {
      this.querySelectorAll('[data-plan-cards-buy]').forEach((button) => {
        this.setLoading(button, false);
        this.setError(button, '');
      });
    }

    get rootUrl() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    get cartAddUrl() {
      return (window.routes && window.routes.cart_add_url) || `${this.rootUrl}cart/add`;
    }

    get cartUrl() {
      return (window.routes && window.routes.cart_url) || `${this.rootUrl}cart`;
    }

    get cart() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    onClick(event) {
      const decrease = event.target.closest('[data-plan-cards-counter-decrease]');
      const increase = event.target.closest('[data-plan-cards-counter-increase]');

      if (decrease || increase) {
        event.preventDefault();
        this.stepCounter(event.target.closest('[data-plan-cards-counter]'), decrease ? -1 : 1);
        return;
      }

      const button = event.target.closest('[data-plan-cards-buy]');
      if (!button || button.disabled) return;

      event.preventDefault();

      const ga4Id = button.dataset.planCardsGa4Id;
      if (ga4Id) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: ga4Id });
      }

      if (this.action === 'permalink') {
        window.location.href = this.buildPermalink(button);
        return;
      }

      this.addToCart(button);
    }

    buildPermalink(button) {
      const variantId = button.dataset.planCardsVariant;
      const quantity = Number(button.dataset.planCardsQuantity) || 1;
      const sellingPlan = button.dataset.planCardsSellingPlan;

      let permalink = `${this.rootUrl}cart/${variantId}:${quantity}`;
      if (sellingPlan) permalink += `?selling_plan=${sellingPlan}`;
      return permalink;
    }

    initCounters() {
      this.querySelectorAll('[data-plan-cards-counter]').forEach((counter) => this.syncCounter(counter));
    }

    stepCounter(counter, delta) {
      if (!counter) return;

      const min = Number(counter.dataset.planCardsQuantityMin) || 1;
      const max = Number(counter.dataset.planCardsQuantityMax) || min;
      const valueElement = counter.querySelector('[data-plan-cards-counter-value]');
      if (!valueElement) return;

      const current = Number(valueElement.textContent) || min;
      const next = Math.min(max, Math.max(min, current + delta));
      if (next === current) return;

      valueElement.textContent = next;
      this.syncCounter(counter);
    }

    syncCounter(counter) {
      const min = Number(counter.dataset.planCardsQuantityMin) || 1;
      const max = Number(counter.dataset.planCardsQuantityMax) || min;
      const valueElement = counter.querySelector('[data-plan-cards-counter-value]');
      const decreaseButton = counter.querySelector('[data-plan-cards-counter-decrease]');
      const increaseButton = counter.querySelector('[data-plan-cards-counter-increase]');
      if (!valueElement) return;

      const quantity = Number(valueElement.textContent) || min;
      if (decreaseButton) decreaseButton.disabled = quantity <= min;
      if (increaseButton) increaseButton.disabled = quantity >= max;

      const card = counter.closest('.plan-cards__card');
      if (!card) return;

      const buyButton = card.querySelector('[data-plan-cards-buy]');
      if (buyButton) buyButton.dataset.planCardsQuantity = quantity;

      const totalElement = card.querySelector('[data-plan-cards-total-value]');
      if (!totalElement) return;

      let totals = {};
      try {
        totals = JSON.parse(counter.dataset.planCardsTotals || '{}');
      } catch (error) {
        totals = {};
      }

      if (totals[quantity] != null) totalElement.textContent = totals[quantity];
    }

    addToCart(button) {
      const item = { id: Number(button.dataset.planCardsVariant), quantity: Number(button.dataset.planCardsQuantity) || 1 };
      const sellingPlan = button.dataset.planCardsSellingPlan;
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      const cart = this.action === 'cart' ? this.cart : null;
      const body = { items: [item] };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      this.setLoading(button, true);
      this.setError(button, '');

      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.setLoading(button, false);
            this.setError(button, response.description || response.message);
            return;
          }

          if (this.action === 'checkout') {
            window.location.href = `${this.rootUrl}checkout`;
            return;
          }

          if (!cart) {
            window.location.href = this.cartUrl;
            return;
          }

          this.setLoading(button, false);
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);

          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'plan-cards',
              productVariantId: item.id,
              cartData: response,
            });
          }
        })
        .catch((error) => {
          console.error(error);
          this.setLoading(button, false);
          this.setError(button, 'Something went wrong. Please try again.');
        });
    }

    setLoading(button, isLoading) {
      button.classList.toggle('plan-cards__button--loading', isLoading);
      button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    setError(button, message) {
      const target = button.closest('.plan-cards__footer');
      const element = target && target.querySelector('[data-plan-cards-error]');
      if (!element) return;

      element.textContent = message || '';
      element.hidden = !message;
    }
  }

  customElements.define('plan-cards', PlanCards);
}
